import { useCallback, useMemo, useState } from "react";
import { getTimelineEntriesSnapshot } from "../../services/timeline";
import {
  RANGE_PRESET,
  THERAPY_PREP_FILTERS,
  endOfDay,
  formatPeriodLabel,
  getMeaningfulSummary,
  getTherapyPrepCategory,
  getWeekRange,
  getMonthRange,
  isValidDate,
  startOfDay,
  toEntryDate,
} from "../../components/Reports/reportBuilders/dailyCareReportEntryUtils";

const formatTimelineLine = (entry = {}) => {
  const date = toEntryDate(entry.timestamp || entry.date);
  const dateLabel = isValidDate(date)
    ? date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "";
  const category = getTherapyPrepCategory(entry);
  const summary = getMeaningfulSummary(entry);
  if (!summary) return "";
  return [dateLabel, category ? category[0].toUpperCase() + category.slice(1) : "", summary]
    .filter(Boolean)
    .join(" — ");
};

export const filterProfileShareTimelineEntries = ({
  entries = [],
  startDate,
  endDate,
  selectedFilters = [],
} = {}) => entries
  .filter((entry) => {
    const date = toEntryDate(entry.timestamp || entry.date);
    return isValidDate(date) && date >= startOfDay(startDate) && date <= endOfDay(endDate);
  })
  .filter((entry) => selectedFilters.includes(getTherapyPrepCategory(entry)));

export const useProfileShareTimeline = ({ childId = "" } = {}) => {
  const initialRange = useMemo(() => getWeekRange(), []);
  const [rangePreset, setRangePreset] = useState(RANGE_PRESET.WEEK);
  const [customStartDate, setCustomStartDate] = useState(initialRange.start);
  const [customEndDate, setCustomEndDate] = useState(initialRange.end);
  const [selectedFilters, setSelectedFilters] = useState(() => THERAPY_PREP_FILTERS.map((item) => item.key));
  const [entries, setEntries] = useState([]);
  const [status, setStatus] = useState("idle");

  const { rangeStart, rangeEnd } = useMemo(() => {
    const activeRange = rangePreset === RANGE_PRESET.MONTH
      ? getMonthRange()
      : rangePreset === RANGE_PRESET.CUSTOM
        ? { start: customStartDate, end: customEndDate }
        : getWeekRange();
    return {
      rangeStart: startOfDay(activeRange.start),
      rangeEnd: endOfDay(activeRange.end),
    };
  }, [customEndDate, customStartDate, rangePreset]);

  const loadTimeline = useCallback(async () => {
    if (!childId || status === "loading" || status === "ready") return;
    setStatus("loading");
    try {
      const earliest = new Date();
      earliest.setDate(earliest.getDate() - 90);
      const nextEntries = await getTimelineEntriesSnapshot(childId, {
        startDate: earliest,
        endDate: new Date(),
      });
      setEntries(nextEntries);
      setStatus("ready");
    } catch (error) {
      setEntries([]);
      setStatus("error");
    }
  }, [childId, status]);

  const filteredEntries = useMemo(() => filterProfileShareTimelineEntries({
    entries,
    startDate: rangeStart,
    endDate: rangeEnd,
    selectedFilters,
  }), [entries, rangeEnd, rangeStart, selectedFilters]);
  const lines = useMemo(
    () => filteredEntries.map(formatTimelineLine).filter(Boolean).slice(0, 30),
    [filteredEntries]
  );

  const toggleFilter = (filterKey) => setSelectedFilters((current) => (
    current.includes(filterKey)
      ? current.filter((item) => item !== filterKey)
      : [...current, filterKey]
  ));

  return {
    rangePreset,
    setRangePreset,
    customStartDate,
    setCustomStartDate,
    customEndDate,
    setCustomEndDate,
    selectedFilters,
    toggleFilter,
    periodLabel: formatPeriodLabel(rangeStart, rangeEnd, rangePreset),
    lines,
    status,
    loadTimeline,
  };
};
